// Utilidades de diff para el timeline de aprendizaje de plantillas.
// Simple y sin dependencias: convierte el machote HTML de cada sección a texto plano
// y hace un diff palabra-por-palabra (LCS) → tokens marcados eq | add | del.
// Lo consume EscriturasTemplateDetail (EventDiff) para pintar verde/rojo estilo GitHub.

/** HTML del machote → texto plano legible (sin tags), preservando saltos de bloque. */
export function htmlToText(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Cota de seguridad: con textos enormes el LCS O(n·m) se vuelve caro. Por encima,
// caemos a un diff de bloque (todo el viejo en rojo, todo el nuevo en verde).
const MAX_TOKENS = 1500;

/**
 * Diff palabra-por-palabra entre dos textos planos.
 * Devuelve [{ t: 'eq' | 'add' | 'del', v: string }] en orden de lectura.
 */
export function diffTokens(oldText, newText) {
  const a = oldText ? oldText.split(/(\s+)/).filter((s) => s.length > 0) : [];
  const b = newText ? newText.split(/(\s+)/).filter((s) => s.length > 0) : [];

  if (a.length === 0) return b.map((v) => ({ t: 'add', v }));
  if (b.length === 0) return a.map((v) => ({ t: 'del', v }));

  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    return [...a.map((v) => ({ t: 'del', v })), ...b.map((v) => ({ t: 'add', v }))];
  }

  const n = a.length;
  const m = b.length;
  // Tabla LCS (longitudes), llenada desde el final.
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ t: 'eq', v: a[i] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ t: 'del', v: a[i] });
      i += 1;
    } else {
      ops.push({ t: 'add', v: b[j] });
      j += 1;
    }
  }
  while (i < n) {
    ops.push({ t: 'del', v: a[i] });
    i += 1;
  }
  while (j < m) {
    ops.push({ t: 'add', v: b[j] });
    j += 1;
  }
  return ops;
}

/** True si hay al menos un token agregado o quitado (los textos difieren). */
export function hasChanges(ops) {
  return ops.some((o) => o.t !== 'eq');
}
